import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../lib/gsap';
import { serviceTranslations } from '../i18n/serviceTranslations';
import { StaticChip } from './StaticVisuals';
import { useUi } from '../i18n/uiTranslations';

const JourneyContext = createContext({ mode: 'static', chip: null });
export const useJourney = () => useContext(JourneyContext);

export const canWebGL = () => {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) {
    return false;
  }
};

const isMobileLayout = () => window.innerWidth < 1024;

const readAnchors = (root) => {
  const vh = window.innerHeight;
  return [...root.querySelectorAll('[data-scene]')]
    .map((el) => {
      const r = el.getBoundingClientRect();
      const top = r.top + window.scrollY;
      const mode = el.dataset.sceneAt;
      const at = mode === 'start' ? top - vh * 0.9 : mode === 'top' ? top - 80 : top + r.height / 2 - vh / 2;
      return { pos: Math.max(0, at), s: Number(el.dataset.scene) };
    })
    .sort((a, b) => a.pos - b.pos);
};

const progressAt = (anchors, y) => {
  if (!anchors.length) return 0;
  if (y <= anchors[0].pos) return anchors[0].s;
  for (let i = 0; i < anchors.length - 1; i++) {
    const a = anchors[i];
    const b = anchors[i + 1];
    if (y < b.pos) return a.s + (b.s - a.s) * ((y - a.pos) / Math.max(1, b.pos - a.pos));
  }
  return anchors[anchors.length - 1].s;
};

export const Journey = ({ children }) => {
  const [mode, setMode] = useState(() => (prefersReducedMotion() || !canWebGL() ? 'static' : 'loading'));
  const [mobile, setMobile] = useState(isMobileLayout);
  const wrap = useRef(null);
  const holder = useRef(null);
  const canvasRef = useRef(null);
  const expRef = useRef(null);
  const chip = useRef({
    drag: (dx) => expRef.current && expRef.current.chipDrag(dx),
    flip: () => expRef.current && expRef.current.chipFlip(),
    release: () => expRef.current && expRef.current.chipRelease(),
  }).current;

  useEffect(() => {
    const onResize = () => setMobile((m) => (m !== isMobileLayout() ? !m : m));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (mode === 'static') return undefined;
    let exp = null;
    let disposed = false;
    let anchors = [];
    let lastW = 0;
    let lastH = 0;
    const cleanups = [];

    const size = () => {
      const w = holder.current.clientWidth;
      const h = holder.current.clientHeight;
      if (mobile && w === lastW && Math.abs(h - lastH) < 160 && lastH) return;
      lastW = w; lastH = h;
      canvasRef.current.style.height = `${h}px`;
      if (exp) {
        exp.resize(w, h);
        const band = wrap.current.querySelector('[data-stage-band]');
        exp.setFrame(72, band && band.offsetHeight ? band.offsetHeight : 72 + Math.min(w * 0.86, h * 0.52));
      }
    };
    const compute = () => { anchors = readAnchors(wrap.current); if (exp) exp.setTarget(progressAt(anchors, window.scrollY)); };
    const onScroll = () => exp && exp.setTarget(progressAt(anchors, window.scrollY));
    const onPointer = (e) => { if (exp && e.pointerType === 'mouse') exp.setPointer(e.clientX / window.innerWidth - 0.5, e.clientY / window.innerHeight - 0.5); };
    const onVis = () => exp && exp.setActive(!document.hidden);

    const providers = serviceTranslations.en.aggregator.partners;
    import('../three/Experience')
      .then(({ createExperience }) => createExperience(canvasRef.current, {
        mobile,
        providers,
        onReady: () => !disposed && setMode('webgl'),
        onLost: () => !disposed && setMode('static'),
      }))
      .then((e) => {
        if (disposed) { e.dispose(); return; }
        exp = e;
        expRef.current = e;
        size();
        anchors = readAnchors(wrap.current);
        exp.jump(progressAt(anchors, window.scrollY));
        const ro = new ResizeObserver(size);
        ro.observe(holder.current);
        const ro2 = new ResizeObserver(compute);
        ro2.observe(wrap.current);
        const io = new IntersectionObserver(([en]) => exp && exp.setActive(en.isIntersecting && !document.hidden));
        io.observe(wrap.current);
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('pointermove', onPointer, { passive: true });
        window.addEventListener('orientationchange', compute);
        const onOrient = () => { lastH = 0; setTimeout(size, 300); };
        window.addEventListener('orientationchange', onOrient);
        document.addEventListener('visibilitychange', onVis);
        cleanups.push(() => {
          ro.disconnect(); ro2.disconnect(); io.disconnect();
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('pointermove', onPointer);
          window.removeEventListener('orientationchange', compute);
          window.removeEventListener('orientationchange', onOrient);
          document.removeEventListener('visibilitychange', onVis);
        });
      })
      .catch(() => !disposed && setMode('static'));

    return () => {
      disposed = true;
      expRef.current = null;
      cleanups.forEach((c) => c());
      if (exp) exp.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobile, mode === 'static']);

  return (
    <JourneyContext.Provider value={{ mode, chip }}>
      <div ref={wrap} className="relative" data-testid="journey" data-journey-mode={mode}>
        {mode !== 'static' && (
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
            <div ref={holder} className="sticky top-0 h-[100lvh] w-full overflow-hidden">
              <canvas
                key={mobile ? 'm' : 'd'}
                ref={canvasRef}
                className="block w-full h-full transition-opacity duration-1000"
                style={{ opacity: mode === 'webgl' ? 1 : 0 }}
                data-testid="journey-canvas"
              />
              {mode === 'loading' && (
                <div className="absolute inset-x-0 top-0 stage-band flex justify-center items-center lg:h-full lg:pt-0 lg:inset-0 lg:justify-end lg:pr-[14vw]">
                  <StaticChip className="w-[min(80vw,calc(var(--band)*0.95))] lg:w-[26vw] lg:max-w-[380px] opacity-80" />
                </div>
              )}
            </div>
          </div>
        )}
        {children}
      </div>
    </JourneyContext.Provider>
  );
};

export const ChipControl = ({ className = '', testId }) => {
  const { mode, chip } = useJourney();
  const u = useUi();
  const drag = useRef(null);
  if (mode !== 'webgl') return null;
  const end = (tap) => {
    if (!drag.current) return;
    drag.current = null;
    if (tap) chip.flip(); else chip.release();
  };
  return (
    <button
      type="button"
      aria-label={u('flipChip')}
      title={u('flipChip')}
      className={`aspect-square rounded-full touch-pan-y cursor-grab active:cursor-grabbing select-none ${className}`}
      onPointerDown={(e) => { drag.current = { x: e.clientX, moved: 0 }; e.currentTarget.setPointerCapture(e.pointerId); }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const dx = e.clientX - drag.current.x;
        drag.current.x = e.clientX;
        drag.current.moved += Math.abs(dx);
        chip.drag(dx);
      }}
      onPointerUp={() => end(drag.current && drag.current.moved < 6)}
      onPointerCancel={() => end(false)}
      onClick={(e) => { if (e.detail === 0) chip.flip(); }}
      data-testid={testId}
    />
  );
};

const SPANS = {
  5: ['lg:col-span-5', 'lg:col-span-7'],
  6: ['lg:col-span-6', 'lg:col-span-6'],
  7: ['lg:col-span-7', 'lg:col-span-5'],
};

export const SceneSection = ({ scene, id, testId, span = 5, staticVisual, children, sceneAt, chipControl }) => {
  const { mode } = useJourney();
  const [copy, stage] = SPANS[span];
  return (
    <section id={id} data-scene={scene} data-scene-at={sceneAt} data-testid={testId} className="relative z-10">
      <div className="lg:hidden stage-band flex items-center justify-center" data-stage-band aria-hidden={chipControl ? undefined : 'true'}>
        {mode === 'static' && staticVisual}
        {chipControl && <ChipControl testId={`${testId}-chip-control-mobile`} className="w-[min(80vw,calc(var(--band)*0.95))]" />}
      </div>
      <div className="relative bg-[#080808] lg:bg-transparent lg:min-h-[100svh] lg:flex lg:items-center">
        <div className="lg:hidden absolute -top-4 inset-x-0 h-4 bg-gradient-to-b from-transparent to-[#080808] pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto w-full px-6 lg:px-12 pt-6 pb-20 lg:py-28 grid lg:grid-cols-12 gap-10">
          <div className={copy}>{children}</div>
          <div className={`hidden lg:flex ${stage} items-center justify-center`} aria-hidden={chipControl ? undefined : 'true'}>
            {mode === 'static' && <div className="w-full max-w-[520px]">{staticVisual}</div>}
            {chipControl && <ChipControl testId={`${testId}-chip-control`} className="w-[min(26vw,380px)]" />}
          </div>
        </div>
      </div>
    </section>
  );
};
